import React, { useMemo } from "react";
import {
  Search,
  ChevronDown,
  ChevronRight,
  Pencil,
  PanelLeftClose,
} from "lucide-react";

// Three-state semantics: Filled (green) · Needs review (amber) · Empty (gray).
const dotClassFor = (status) =>
  status === "answered" || status === "user"
    ? "bg-[#22C55E]"
    : status === "review"
    ? "bg-[#FBBF24]"
    : "bg-white/25";

// Clickable filter chips — double as the legend AND let the user focus on
// exactly the fields that need attention (review / empty) on long forms.
const FILTERS = [
  { key: "all", label: "All", dot: null },
  { key: "filled", label: "Filled", dot: "#22C55E" },
  { key: "review", label: "Review", dot: "#FBBF24" },
  { key: "empty", label: "Empty", dot: "#9CA3AF" },
];

const FilterChips = ({ value, onChange, stats }) => {
  const counts = {
    all: stats.total,
    filled: stats.done,
    review: stats.review,
    empty: stats.unanswered,
  };
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {FILTERS.map(({ key, label, dot }) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            aria-pressed={active}
            className={`inline-flex items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
              active
                ? "bg-white/15 border-white/25 text-white"
                : "border-white/10 text-[#A095BE] hover:bg-white/5 hover:text-white"
            }`}
          >
            {dot && (
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: dot }}
              />
            )}
            {label}
            <span
              className={`min-w-[16px] text-center text-[10px] px-1 rounded-full ${
                active ? "bg-white/20 text-white" : "bg-white/5 text-[#7A7090]"
              }`}
            >
              {counts[key] ?? 0}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const FieldNavigatorSidebar = ({
  loading,
  job,
  stats,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  sidebarSections,
  collapsedSections,
  toggleSection,
  getFieldStatus,
  highlightedFieldId,
  hoveredFieldId,
  setHoveredFieldId,
  isFinalizedView,
  onFieldRowClick,
  onOpenEditor,
  collapsed,
  onCollapse,
}) => {
  // Stable "Field N" numbering from the job's field order — used as a friendly
  // fallback for fields without a label (e.g. manually-added boxes).
  const fieldNumberById = useMemo(() => {
    const map = {};
    (job?.fields || []).forEach((f, i) => {
      map[f.id] = i + 1;
    });
    return map;
  }, [job?.fields]);

  const displayLabel = (field) =>
    field.label?.trim()
      ? field.label
      : `Field ${fieldNumberById[field.id] || ""}`.trim();

  // Collapsed → the "Fields" entry in the left tool rail re-opens it.
  if (collapsed) return null;

  return (
    <aside className="w-[240px] flex-shrink-0 bg-[#1F1A2E] text-[#E5E1F0] flex flex-col">
      <div className="px-4 py-4 border-b border-white/5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#A095BE]">
            Form Fields
          </div>
          <button
            type="button"
            onClick={onCollapse}
            title="Collapse fields"
            className="p-1 rounded hover:bg-white/10 text-[#A095BE] hover:text-white"
          >
            <PanelLeftClose size={16} />
          </button>
        </div>
        <div className="flex items-baseline justify-between text-[12px] text-[#A095BE] mb-2">
          <span>
            <span className="font-semibold text-white text-[15px]">{stats.done}</span>
            <span> / {stats.total} filled</span>
          </span>
          {stats.total > 0 && (
            <span className="text-[11px] text-[#7A7090]">
              {Math.round((stats.done / stats.total) * 100)}%
            </span>
          )}
        </div>
        {/* Progress bar */}
        {stats.total > 0 && (
          <div className="h-1.5 w-full rounded-full bg-white/10 mb-3 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#22C55E] to-[#16A34A] rounded-full transition-all duration-500"
              style={{ width: `${Math.round((stats.done / stats.total) * 100)}%` }}
            />
          </div>
        )}
        <div className="mb-3">
          <FilterChips value={statusFilter} onChange={setStatusFilter} stats={stats} />
        </div>
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A095BE]"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search fields..."
            aria-label="Search fields"
            className="w-full pl-8 pr-3 py-1.5 text-[13px] bg-white/5 border border-white/10 rounded text-white placeholder-[#7A7090] focus:outline-none focus:border-[#C95EBE]"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-[13px] text-[#A095BE]">
            Loading fields...
          </div>
        ) : sidebarSections.length === 0 ? (
          <div className="p-6 text-center text-[13px] text-[#A095BE]">
            {(job?.fields || []).length === 0
              ? "No fields yet — detect fields or add one to get started."
              : statusFilter !== "all"
              ? `No ${statusFilter} fields${searchQuery ? " match your search" : ""}.`
              : "No fields match your search."}
          </div>
        ) : (
          sidebarSections.map(([section, fields]) => {
            const isCollapsed = collapsedSections.has(section);
            return (
              <div key={section}>
                <button
                  type="button"
                  onClick={() => toggleSection(section)}
                  className="w-full flex items-center justify-between px-4 py-2 hover:bg-white/5 text-left text-[11px] font-semibold text-[#A095BE] uppercase tracking-[0.1em] border-b border-white/5"
                >
                  <span className="flex items-center gap-1.5 truncate">
                    {isCollapsed ? (
                      <ChevronRight size={14} />
                    ) : (
                      <ChevronDown size={14} />
                    )}
                    <span className="truncate">{section}</span>
                  </span>
                  <span className="text-[11px] font-medium text-[#7A7090] ml-2 flex-shrink-0">
                    {fields.length}
                  </span>
                </button>

                {!isCollapsed &&
                  fields.map((field) => {
                    const status = getFieldStatus(field);
                    const isHighlighted = highlightedFieldId === field.id;
                    return (
                      <div
                        key={field.id}
                        onClick={() => onFieldRowClick(field)}
                        onMouseEnter={() => setHoveredFieldId(field.id)}
                        onMouseLeave={() => setHoveredFieldId(null)}
                        className={`flex items-start gap-2.5 px-4 py-2.5 hover:bg-white/5 cursor-pointer border-b border-white/5 group transition-colors ${
                          isHighlighted
                            ? "bg-[#C95EBE]/15 border-l-2 border-l-[#C95EBE]"
                            : "border-l-2 border-l-transparent"
                        }`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${dotClassFor(
                            status
                          )}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium text-white truncate">
                            {displayLabel(field)}
                          </div>
                          {field.answer != null &&
                          field.answer.toString().trim() !== "" ? (
                            <div className="text-[11px] text-[#A095BE] truncate mt-0.5">
                              {String(field.answer)}
                            </div>
                          ) : (
                            <div className="text-[11px] text-[#6B6580] italic truncate mt-0.5">
                              {status === "review" ? "Needs review" : "Empty"}
                            </div>
                          )}
                        </div>
                        {!isFinalizedView && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenEditor(field);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-white/10 rounded text-[#A095BE] hover:text-white flex-shrink-0"
                            title="Edit answer"
                          >
                            <Pencil size={12} />
                          </button>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
};

export default FieldNavigatorSidebar;
